import { createDealerAxios } from '../factories/axios-factory';
import { useMapProposal } from '../functions';
import { useAuthStore } from '../stores/auth-store';
import { LoanApplication } from 'src/types/loan-application';
import type {
  ApplicationNote, CreditCheck, PayoutPackage,
  ApplicationInfo, ConvertFullProposalRequest,
  Vehicle, Enquiry
} from 'src/types/loan-application/index';
import type {
  QuoteSendRequest, QuoteProceed,
  ResidualsRequest, QuickQuote, Quote
} from 'src/types/quote';
import type { GetApplicationsRequest, GetEnquiriesRequest } from 'src/types/dealer';
import { QuoteSendRequestOld } from 'src/types/quote/quote-send-old';
import { PortalNote } from 'src/types/notification/portal-note';
import { ResidualsResponse } from 'src/types/quote/residuals-response';
import { ResidualResponse } from 'src/types/quote/residual-response';
import { Proposal } from '@cf247/pricing';

const backendUser = createDealerAxios(process.env.DealersApiUrl!);

let ws = null as null | WebSocket;

const proposalService = {
  createApplication(proposal: LoanApplication) {

    proposal.isV2App = true;

    //@ts-ignore
    proposal.affiliateUserId = proposal.affiliateUserId.toString();

    return new Promise<string>((resolve, reject) => {
      backendUser.post(
        `/application`,
        proposal
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data.id as string);
          return;
        }
        reject();
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getApplications(request: GetApplicationsRequest) {
    return new Promise<LoanApplication[]>((resolve, reject) => {
      backendUser.post(
        `/applications`,
        request
      ).then(res => {
        if (res.status !== 200) {
          resolve([]);
          return;
        }
        if (!res.data || !res.data.length) {
          resolve([]);
          return;
        }
        resolve(res.data.map((a: LoanApplication) => {

          if (!a.dealerId) {
            a.dealerId = useAuthStore().dealers?.length ? useAuthStore().dealers!.find(de => de.affiliateUserId.toString() === a.affiliateUserId)?.dealerId : useAuthStore().dealerId;
          }

          if (a.vehicle && a.vehicle.vrm === "" && a.vehicle.make === "" && a.vehicle.model === "" && a.vehicle.price === 0) {
            a.vehicle = null;
          }

          return new LoanApplication(a);
        }));
      }).catch(error => {
        reject(error);
      })
    })
  },
  getApplication(brokerReference: number, affiliateUserId: number) {
    return new Promise<LoanApplication>((resolve, reject) => {
      backendUser.get(
        `/application-single/${brokerReference}?affiliateUserId=${affiliateUserId}`
      ).then(res => {
        if (res.status === 200) {
          if (!res.data.dealerId) {
            res.data.dealerId = useAuthStore().dealers?.length ? useAuthStore().dealers!.find(de => de.affiliateUserId === res.data.affiliateUserId)?.dealerId : useAuthStore().dealerId;
          }
          if (res.data.vehicle && res.data.vehicle.vrm === "" && res.data.vehicle.make === "" && res.data.vehicle.model === "" && res.data.vehicle.price === 0) {
            res.data.vehicle = null;
          }
          resolve(new LoanApplication(res.data));
          return;
        }
        reject(null);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  createEnquiry(enquiry: Enquiry){
    return new Promise<string>((resolve, reject) => {
      backendUser.post(
        `/enquiry`,
        enquiry
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data.id as string);
          return;
        }
        reject();
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getEnquiries(request: GetEnquiriesRequest){
    return new Promise<Enquiry[]>((resolve, reject) => {
      backendUser.post(
        `/enquiries`,
        request
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as Enquiry[]);
          return;
        }
        if(res.status === 204){
          resolve([]);
          return;
        }
        resolve([]);
      })
    })
  },
  deleteEnquiry(enquiryId: number){
    return new Promise<boolean>((resolve, reject) => {
      backendUser.delete(
        `/enquiry/${enquiryId}`
      ).then(res => {
        if (res.status === 200) {
          resolve(true);
          return;
        }
        resolve(false);
      }).catch(error => {
        reject(error);
      })
    })
  },
  createQuickQuote(quote: QuickQuote) {
    return new Promise<string>((resolve, reject) => {
      //@ts-ignore
      quote.affiliateUserId = quote.affiliateUserId.toString();
      backendUser.post(
        `/quick-quote`,
        quote
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as string);
          return;
        }
        reject();
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getQuickQuotes(aids: string[], days: number) {
    return new Promise<QuickQuote[]>((resolve, reject) => {
      backendUser.post(
        `/quick-quotes`,
        {
          affiliateUserIds: aids,
          days
        }
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data.map((q: QuickQuote) => {
            if (!q.dealerId) {
              q.dealerId = useAuthStore().dealers?.length ? useAuthStore().dealers!.find(de => de.affiliateUserId.toString() === q.affiliateUserId)?.dealerId : useAuthStore().dealerId;
            }
            return q;
          }));
          return;
        }
        if (res.status === 204) {
          resolve([]);
          return;
        }
        resolve([]);
      })
        .catch(error => {
          resolve([]);
        })
    })
  },
  getApplicationInfo(brokerReference: number) {
    return new Promise<ApplicationInfo>((resolve, reject) => {
      backendUser.get(
        `/application-info/${brokerReference}`
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as ApplicationInfo);
          return;
        }
        reject(null);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getPayoutPackage(applicationId: number, aid: string) {
    return new Promise<PayoutPackage>((resolve, reject) => {
      backendUser.get(
        `/payout/${applicationId}/${aid}`,
        {
          headers: {
            affiliateUserId: Number(aid)
          }
        }
      ).then(response => {
        const p = response.data as PayoutPackage;
        if (!p.customerRequirements) {
          p.customerRequirements = [];
        }
        if (!p.customerMiscRequirements) {
          p.customerMiscRequirements = [];
        }
        if (p.customerRequirements && p.customerRequirements.length > 0) {
          p.customerRequirements = p.customerRequirements.filter(c => c.requirementTypeId !== 167);
        }
        if (p.customerMiscRequirements && p.customerMiscRequirements.length > 0) {
          p.customerMiscRequirements = p.customerMiscRequirements.filter(c => c.requirementTypeId !== 167);
        }
        resolve(p);
      }).catch(error => {
        reject(error)
      })
    })
  },
  getPackStatus(applicationId: number) {
    return new Promise<any>((resolve, reject) => {
      backendUser.get(
        `/pack-status/${applicationId}`
      ).then(response => {
        resolve(response.data);
      }).catch(error => {
        reject(error)
      })
    })
  },
  getLoanDealId(applicationId: number) {
    return new Promise<number>((resolve, reject) => {
      backendUser.get(
        `/loan-deal-id/${applicationId}`
      ).then(response => {
        resolve(response.data as number);
      }).catch(error => {
        reject(error)
      })
    })
  },
  convertToFullProposal(request: ConvertFullProposalRequest) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.patch(
        //'https://localhost:5001/v3/application/convert',
        `/convert`,
        request
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as boolean);
          return;
        }
        reject();
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  changeVehicle(applicationId: number, vehicle: Vehicle) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/application/${applicationId}/vehicle`,
        vehicle
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as boolean);
          return;
        }
        reject();
      }).catch(error => {
        reject(error);
      })
    })
  },
  getAvailableProposals(applicationId: number, expand: boolean, aid: number) {
    return new Promise<Proposal[]>((resolve, reject) => {
      backendUser.get(
        `/quote/proposals-expanded?applicationId=${applicationId}&expandedProposals=${expand}&affiliateUserId=${aid}`
      ).then(res => {

        const did = useAuthStore().dealers?.length ? useAuthStore().dealers!.find(d => d.affiliateUserId === aid)?.dealerId : useAuthStore().dealerId;

        let mapped = useMapProposal(res.data, did)
          .filter(p => p.lenderId !== 62); //remove mann island props

        if (expand) {
          mapped = mapped.filter(m => m.lenderId != 49);
        }

        //mapped.find(p => p.financeCompanyId === 75)!.loanProposalStatus = LoanProposalStatus_Enum[LoanProposalStatus_Enum.Processing]
        // Dealer portal only Alphera min lend amount is 3000
        mapped.forEach(m => {
          if (m.lenderId === 50 && m.minLendAmount === 12500) {
            m.minLendAmount = 3000;
            m.minLendAmountPcp = 3000;
          }
        })

        resolve(mapped);
      })
        .catch(error => {
          resolve([])
        })
    })
  },
  getCreditCheck(applicationId: number, aid: number) {
    return new Promise<CreditCheck>((resolve, reject) => {
      backendUser.get(
        `/credit-check/${applicationId}?affiliateUserId=${aid}`
      ).then(res => {
        resolve(res.data as CreditCheck)
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getResiduals(req: ResidualsRequest) {
    return new Promise<ResidualsResponse | null>((resolve, reject) => {
      backendUser.post(`/v2/vehicle/residuals/multiple`, req).then(res => {
        if (res.status === 204) {
          resolve(null);
        }
        resolve({
          residuals: res.data as ResidualResponse[]
        } as ResidualsResponse);
      }).catch(error => {
        resolve(null);
        resolve(null);
      })
    })
  },
  proceedWithQuote(req: QuoteProceed) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/proceed`,
        req
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  sendQuoteToCustomer(req: QuoteSendRequest) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/send`,
        req
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  sendQuoteToCustomerOld(req: QuoteSendRequestOld) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/send`,
        req
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  sendCustomerEmail(req: QuoteProceed) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/customer/proceed`,
        req
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getUploadedDocuments(applicationId: number) {
    return new Promise<ApplicationNote[]>((resolve, reject) => {
      backendUser.get(
        `/application/documents?brokerReference=${applicationId}`
      ).then(res => {
        resolve(res.data);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getPortalNotes(applicationId: number) {
    return new Promise<PortalNote[]>((resolve, reject) => {
      backendUser.get(
        `/application/portal-notes/${applicationId}`
      ).then(res => {
        resolve(res.data as PortalNote[]);
      })
        .catch(error => {
          resolve([]);
        })
    })
  },
  addNote(note: ApplicationNote) {
    return new Promise<boolean>((resolve, reject) => {
      const form = new FormData();
      form.append("noteText", note.text);
      form.append("createdBy", note.createdBy);
      form.append("affiliateUserId", note.affiliateUserId);
      form.append("dealerId", note.dealerId.toString());
      form.append("dealerName", note.dealerName);
      form.append("isUserNote", "true");

      backendUser.post(
        `/application/notes?${note.brokerReference && note.brokerReference > 0 ? 'brokerReference=' + note.brokerReference : 'cosmoId=' + note.cosmosId}`,
        form,
        {
          headers: { "Content-Type": `multipart/form-data` }
        },
      ).then(res => {
        if (res.status === 200) {
          resolve(true);
          return;
        }
        resolve(false);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  saveQuote(quote: Quote) {
    return new Promise<Quote>((resolve, reject) => {
      backendUser.post(
        `/quote`,
        quote
      ).then(res => {
        resolve(res.data as Quote);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getQuotes(applicationId: number) {
    return new Promise<Quote[]>((resolve, reject) => {
      backendUser.get(
        `/quote/${applicationId}`,
      ).then(res => {
        resolve(res.data as Quote[]);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  proposeQuote(applicationId: number, quoteId: number) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/${applicationId}/${quoteId}/propose`,
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          console.log("accept error", error);
          reject(error);
        })
    })
  },
  acceptQuote(applicationId: number, quoteId: number) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/${applicationId}/${quoteId}/accept`,
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          console.log("accept error", error);
          reject(error);
        })
    })
  },
  archiveQuote(applicationId: number, quoteId: number) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.put(
        `/quote/archive/${applicationId}/${quoteId}`,
        {
          isArchived: false
        }
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          console.log("accept error", error);
          reject(error);
        })
    })
  },
  requestQuote(quote: Quote) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/request`,
        quote
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  // getSystemNotes(applicationId: number){
  //   return new Promise<Note[]>((resolve, reject) => {
  //     backendUser.get(
  //       `/notes/${applicationId}/system`
  //     ).then(res => {
  //       resolve(res.data as Note[]);
  //     })
  //       .catch(error => {
  //         resolve([]);
  //       })
  //   })
  // },

  // getUserNotes(applicationId: number){
  //   return new Promise<Note[]>((resolve, reject) => {
  //     backendUser.get(
  //       `/notes/${applicationId}/user`
  //     ).then(res => {
  //       resolve(res.data as Note[]);
  //     })
  //       .catch(error => {
  //         resolve([] as Note[]);
  //       })
  //   })
  // },
  // subscribeGlobal() {
  //   if (ws) {
  //     ws.close();
  //   }
  //   return new Promise<string>((resolve, reject) => {
  //     backendUser.get(`/subscribe-global`)
  //       .then((response) => {
  //         if (response.status === 200) {
  //           resolve(response.data.url as string);
  //         }
  //         reject(null);
  //       })
  //       .catch((error) => {
  //         console.log("ps error: ", error);
  //         reject(error);
  //       });
  //   });
  // },
  toggleNTU(brokerReference: number, isNtu: boolean) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.patch(
        `/toggle-ntu-flag?brokerReference=${brokerReference}&ntuFlagged=${isNtu}`,
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as boolean);
          return;
        }
        reject();
      })
        .catch(error => {
          console.log("mdqqe:", error)
          resolve(false);
        })
    })
  },
  deleteQuickQuote(quoteId: string) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.delete(
        `/quote/quote-delete/${quoteId} `,
      ).then(res => {
        if (res.status === 204 || res.status === 200) {
          resolve(true);
          return;
        }
        reject();
      })
        .catch(error => {
          reject(error);
        });
    });
  },
  getierarchy(balanceToFinance: number, rnScore: number, term: number) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/hierarchy-tiers`,
        JSON.stringify({
          affiliateUser : null,
          customer: {
                rnScore: rnScore
          },
          amountToFinance: balanceToFinance,
          term: term
        })
      ).then(res => {
        if (res.status === 204 || res.status === 200) {
          resolve(true);
          return;
        }
        reject();
      })
        .catch(error => {
          reject(error);
        });
    });
  }
};


export default proposalService;


